import React from 'react';
import { debounce } from 'lodash';
import { datadaInnAutocomplete } from 'app/contact/action-creators';
import useDispatch from './useDispatch';

type ResultItem = {
  label: string;
  value: string;
  inn: string;
};

type CompanyResult = {
  key: string;
  inn: string;
  ogrn?: string;
  kpp?: string;
  legalAddress?: string;
  title?: string;
  opfShort?: string;
  opfFull?: string;
  titleShort?: string;
  titleFull?: string;
  companyName?: string;
};

type RawSuggest = {
  value: string,
  data: {
    inn: string,
    name?: { short?: string },
    opf?: { short?: string },
  }
};

const buildTitle = (suggest: RawSuggest): string => {
  let title = suggest.value;
  if (suggest.data.name?.short) {
    title = suggest.data.name.short;

    if (suggest.data.opf?.short) {
      title += `, ${suggest.data.opf.short}`;
    }
  }
  return title;
};

// Компании могут иметь совпадающий ИНН (например, разные записи одного юрлица),
// поэтому в качестве идентификатора подсказки используется связка ИНН + название.
const buildSuggestKey = (inn: string, title: string): string => `${inn}__${title}`;

export default function () {
  const dispatch = useDispatch();
  const [fetching, setFetching] = React.useState(false);
  const [results, setResults] = React.useState<ResultItem[]>([]);
  const [companyObjects, setCompanyObjects] = React.useState<CompanyResult[]>([]);

  const fetchCompany = (query?: string) => {
    if (!query || query.length < 3) return;
    setResults([]);
    setFetching(true);

    dispatch(datadaInnAutocomplete(query)).then(
      (response: any) => {
        if (response.suggestions) {
          setCompanyObjects(response.suggestions.map((suggest: any) => {
            const title = buildTitle(suggest);
            return {
              key: buildSuggestKey(suggest.data.inn, title),
              inn: suggest.data.inn,
              ogrn: suggest.data.ogrn,
              kpp: suggest.data.kpp,
              legalAddress: suggest.data.address?.value || '',
              title,
              opfShort: suggest.data.opf.short,
              opfFull: suggest.data.opf.full,
              titleShort: suggest.data.name.short,
              titleFull: suggest.data.name.full,
              companyName: title,
            };
          }));
          setResults(response.suggestions.map((suggest: RawSuggest) => {
            const label = buildTitle(suggest);

            return {
              value: buildSuggestKey(suggest.data.inn, label),
              label,
              inn: suggest.data.inn,
            };
          }));
        }
        setFetching(false);
      },
    );
  };

  const getResultData = (key: string): Omit<CompanyResult, 'key'> & { counterpartyId?: string } | null => {
    const result = companyObjects.find((_) => _.key === key);
    if (!result) return null;
    // key нужен только для внутреннего поиска подсказки и не должен попадать
    // в форму/бэкенд как значение, поэтому исключаем его из результата.
    const { key: _matchedKey, ...resultWithoutKey } = result;
    return { ...resultWithoutKey, counterpartyId: undefined };
  };

  const handleSearch = React.useCallback(
    debounce(fetchCompany, 800),
    [fetchCompany],
  );

  return {
    suggestions: results, handleSearch, getResultData, fetching, companyObjects,
  };
}
